import { SubscriptionTier } from "~~/package/mapper/subscription/model/SubscriptionTierType";

export enum AdvancedAssessmentType {
    CORE = "CORE",
    AI_FLUENT = "AI_FLUENT",
    E2E = "E2E",
    AUTOMOTIVE = "AUTOMOTIVE"
}

export const ADVANCED_ASSESSMENT_TYPE_LABEL_KEY: Record<AdvancedAssessmentType, string> = {
    [AdvancedAssessmentType.CORE]: "skillCertify-core-label",
    [AdvancedAssessmentType.E2E]: "skillCertify-e2e-label",
    [AdvancedAssessmentType.AI_FLUENT]: "skillCertify-aI-fluent-label",
    [AdvancedAssessmentType.AUTOMOTIVE]: "skillCertify-automotive-label"
};

export const ADVANCED_ASSESSMENT_TYPE_LABEL_CLASS: Record<AdvancedAssessmentType, string> = {
    [AdvancedAssessmentType.CORE]: "label--d-blue",
    [AdvancedAssessmentType.E2E]: "label--d-e2e",
    [AdvancedAssessmentType.AI_FLUENT]: "label--d-purple",
    [AdvancedAssessmentType.AUTOMOTIVE]: "label--d-automotive"
};

// ProSkillAssureSettingsCommonAssessmentSetsCardTableTrackLevel 전용 아이콘 포함 배지 클래스
// (ADVANCED_ASSESSMENT_TYPE_LABEL_CLASS와는 별개의 스타일 — 다른 컴포넌트에서 사용)
export const ADVANCED_ASSESSMENT_TYPE_BADGE_CLASS: Record<AdvancedAssessmentType, string> = {
    [AdvancedAssessmentType.CORE]: "label--badge-blue",
    [AdvancedAssessmentType.E2E]: "label--badge-e2e",
    [AdvancedAssessmentType.AI_FLUENT]: "label--badge-purple",
    [AdvancedAssessmentType.AUTOMOTIVE]: "label--badge-automotive"
};

// 라벨 아이콘 fill 색상 — 각 타입의 텍스트 색상과 동일하게 맞춘다 (opacity 0.45로 렌더링됨)
export const ADVANCED_ASSESSMENT_TYPE_ICON_FILL: Record<AdvancedAssessmentType, string> = {
    [AdvancedAssessmentType.CORE]: "#7FABFF",
    [AdvancedAssessmentType.E2E]: "#65AF99",
    [AdvancedAssessmentType.AI_FLUENT]: "#B48DF6",
    [AdvancedAssessmentType.AUTOMOTIVE]: "#BF9169"
};

// 백엔드가 아직 assessmentContentType을 내려주지 않거나(undefined) 알 수 없는 값을 보내는 경우를 대비해
// CORE로 안전하게 폴백한다. `createModel`은 배열 필드(sets)의 각 항목까지 모델 기본값을 적용하지 않으므로
// 원본 API 응답 필드가 비어 있으면 여기서 방어해야 t(undefined) 같은 message-compiler 크래시를 막을 수 있다.
export const getAdvancedAssessmentTypeLabelKey = (type?: AdvancedAssessmentType | null): string =>
    ADVANCED_ASSESSMENT_TYPE_LABEL_KEY[type as AdvancedAssessmentType] ?? ADVANCED_ASSESSMENT_TYPE_LABEL_KEY[AdvancedAssessmentType.CORE];

export const getAdvancedAssessmentTypeLabelClass = (type?: AdvancedAssessmentType | null): string =>
    ADVANCED_ASSESSMENT_TYPE_LABEL_CLASS[type as AdvancedAssessmentType] ?? ADVANCED_ASSESSMENT_TYPE_LABEL_CLASS[AdvancedAssessmentType.CORE];

export const getAdvancedAssessmentTypeIconFill = (type?: AdvancedAssessmentType | null): string =>
    ADVANCED_ASSESSMENT_TYPE_ICON_FILL[type as AdvancedAssessmentType] ?? ADVANCED_ASSESSMENT_TYPE_ICON_FILL[AdvancedAssessmentType.CORE];

export const getAdvancedAssessmentTypeBadgeClass = (type?: AdvancedAssessmentType | null): string =>
    ADVANCED_ASSESSMENT_TYPE_BADGE_CLASS[type as AdvancedAssessmentType] ?? ADVANCED_ASSESSMENT_TYPE_BADGE_CLASS[AdvancedAssessmentType.CORE];

export interface AdvancedAssessmentTypeHelpContent {
    title: string;
    description: string;
}

export const ADVANCED_ASSESSMENT_TYPE_HELP_CONTENT: Record<AdvancedAssessmentType, AdvancedAssessmentTypeHelpContent> = {
    [AdvancedAssessmentType.CORE]: {
        title: "pricing-core-assessment-filter-description",
        description: "pricing-core-assessment-filter-description-1"
    },
    [AdvancedAssessmentType.E2E]: {
        title: "pricing-advanced-assessment-filter-description",
        description: "pricing-advanced-assessment-filter-description-1"
    },
    [AdvancedAssessmentType.AI_FLUENT]: {
        title: "pricing-ai-agent-assessment-filter-description",
        description: "pricing-ai-agent-assessment-filter-description-2"
    },
    [AdvancedAssessmentType.AUTOMOTIVE]: {
        title: "pricing-automotive-assessment-filter-description",
        description: "pricing-automotive-assessment-filter-description-1"
    }
};

export const getAdvancedAssessmentTypeHelpContent = (type?: AdvancedAssessmentType | null): AdvancedAssessmentTypeHelpContent =>
    ADVANCED_ASSESSMENT_TYPE_HELP_CONTENT[type as AdvancedAssessmentType] ?? ADVANCED_ASSESSMENT_TYPE_HELP_CONTENT[AdvancedAssessmentType.CORE];

export const isConflictingContentType = (a?: AdvancedAssessmentType | null, b?: AdvancedAssessmentType | null): boolean => {
    const isAiFluent = (type?: AdvancedAssessmentType | null) => (type ?? AdvancedAssessmentType.CORE) === AdvancedAssessmentType.AI_FLUENT;
    return isAiFluent(a) !== isAiFluent(b);
};

// PAY-GATE-07: 고급 평가(CORE 외 콘텐츠 유형) Basic 플랜 잠금 판정.
export const isBlockedByPlan = (type: AdvancedAssessmentType | null | undefined, planTier?: SubscriptionTier): boolean =>
    (type ?? AdvancedAssessmentType.CORE) !== AdvancedAssessmentType.CORE && planTier === SubscriptionTier.BASIC;

// CORE는 3개까지 선택해도 1크레딧(4개부터 2크레딧)이지만, E2E/AI_FLUENT/AUTOMOTIVE는 1개당 1크레딧이 소모된다.
const CORE_CREDIT_BUNDLE_SIZE = 3;

// 산정 기준 팝업에 노출되는 고정 순서 — Core → E2E → AI Fluent → Automotive
const ADVANCED_ASSESSMENT_TYPE_ORDER: AdvancedAssessmentType[] = [
    AdvancedAssessmentType.CORE,
    AdvancedAssessmentType.E2E,
    AdvancedAssessmentType.AI_FLUENT,
    AdvancedAssessmentType.AUTOMOTIVE
];

const calculateCreditForType = (type: AdvancedAssessmentType, count: number): number =>
    type === AdvancedAssessmentType.CORE ? Math.ceil(count / CORE_CREDIT_BUNDLE_SIZE) : count;

export interface AdvancedAssessmentTypeCreditGroup {
    type: AdvancedAssessmentType;
    count: number;
    credit: number;
}

// 유형별로 묶어 선택 개수와 크레딧을 계산 (ex. Core 2개 → {type: CORE, count: 2, credit: 1})
export const groupAssessmentContentTypesByCredit = (assessmentContentTypes: (AdvancedAssessmentType | undefined)[]): AdvancedAssessmentTypeCreditGroup[] => {
    const counts = new Map<AdvancedAssessmentType, number>();
    assessmentContentTypes.forEach(type => {
        const resolvedType = type ?? AdvancedAssessmentType.CORE;
        counts.set(resolvedType, (counts.get(resolvedType) ?? 0) + 1);
    });

    return ADVANCED_ASSESSMENT_TYPE_ORDER
        .filter(type => counts.has(type))
        .map(type => {
            const count = counts.get(type) as number;
            return { type, count, credit: calculateCreditForType(type, count) };
        });
};

export const calculateRequiredInvitationCreditWith = (assessmentContentTypes: (AdvancedAssessmentType | undefined)[]): number =>
    groupAssessmentContentTypesByCredit(assessmentContentTypes).reduce((sum, group) => sum + group.credit, 0);
